import type { CategoryData } from 'service/category/model.ts';
import {
	filters,
	getCategoriesSort,
	remoteFilterToSearchCategoriesParams,
	remoteFilterToSearchUserCategoriesParams,
	type RemoteFilter,
} from 'signals/filters';
import { getOffers, type GetOffersResponse, getOffersSearch } from 'service/offer/resource';
import { searchCategories, type SearchCategoriesParams, userCategories } from 'service/category/resource';
import {
	CATEGORY_LIMIT,
	getEshopCategoriesCounts,
	getSellerCategoriesCounts,
	getUserCategoriesCounts,
	getUserCategoriesFull,
} from 'service/category';
import { phraseCategories, availableCategories } from 'components/filters/categoryList/signals.ts';
import { categoriesMap } from 'signals/category';
import { offers } from 'signals/offers';
import { getUnpackedCategories } from 'service/unpacked';

export async function queryOffersSearchCategories(filterParams: RemoteFilter) {
	const categorySearchParams: SearchCategoriesParams = {
		...remoteFilterToSearchCategoriesParams(filterParams),
		limit: CATEGORY_LIMIT.toString(),
		sort: getCategoriesSort(filters.peek()),
		offset: '0',
	};
	if (!categorySearchParams.category_id) {
		// category.id === 0 => all categories => the endpoint does not work with that, instead we need to omit the id
		delete categorySearchParams.category_id;
	} else {
		// When we filter by a category that is a leaf (no children), we want to show its siblings instead
		const currentCategory = categoriesMap.peek()[Number(filterParams.category_id)];
		if (currentCategory?.children.length === 0 && currentCategory.parent) {
			categorySearchParams.category_id = currentCategory.parent.id.toString();
		}
	}

	return await searchCategories(categorySearchParams);
}

export async function queryOffersBazarCategories(filterParams: RemoteFilter) {
	return await getSellerCategoriesCounts(
		availableCategories.peek(),
		remoteFilterToSearchCategoriesParams(filterParams),
	);
}

/**
 * Does not rebuild the whole category tree structure, but reuses the existing one in
 * {@link availableCategories} signal. Only the offer counts are updated on the currently displayed category tree.
 * This is required due to filters. The result of the underlying request would completely omit a category that would
 * have no offers due to all of them being filtered away. However, in the UI we need to keep the category, but
 * display 0. The `availableCategories` is used as a final list of categories to display, showing an actual offer count
 * or 0. Used when an admin of a bazar changes any filters besides the offer status.
 * @param filterParams
 */
export async function queryOffersAdminCategoriesCounts(filterParams: RemoteFilter) {
	return await getUserCategoriesCounts(
		availableCategories.peek(),
		remoteFilterToSearchUserCategoriesParams(filterParams),
	);
}

/**
 * Heavy version of {@link queryOffersAdminCategoriesCounts}, that also returns updated available category structure.
 * Used when an admin of a bazar changes the offer status filter, which needs a different set of categories to be
 * displayed.
 * @param filterParams
 */
export async function queryOffersAdminCategoriesFull(filterParams: RemoteFilter) {
	return await getUserCategoriesFull(remoteFilterToSearchUserCategoriesParams(filterParams));
}

export async function queryOffersEshopCategories(filterParams: RemoteFilter) {
	return await getEshopCategoriesCounts(availableCategories.peek(), { ...filterParams });
}

export async function queryOffersSearchEshopCategories() {
	return getUnpackedCategories(filters.peek(), categoriesMap.peek());
}

export async function queryOffersAndCategories(appendOffers: boolean = false) {
	const filtersValue = filters.peek();
	const categoryId = filtersValue?.category?.id?.toString();
	const phrase = filtersValue?.hledej.seoName;

	const categoriesPromise = await userCategories({
		limit: '100',
		sort: 'name',
		...(categoryId && { category_id: categoryId }),
		...(phrase && { phrase }),
	});

	const filterParams = filtersValue.isAdmin
		? {
				...filters.localFilterToRemote(),
				include_adverts_stats: 1 as const,
			}
		: {
				...filters.localFilterToRemote(),
			};

	const offerPromise =
		filtersValue.seller && !filtersValue.isAdmin
			? await getOffersSearch(filterParams)
			: await getOffers(filterParams);

	const [offersResult, categoriesResult]: [GetOffersResponse, CategoryData[]?] = await Promise.all([
		offerPromise,
		categoriesPromise,
	]);

	offers.value = appendOffers
		? { ...offers.peek(), results: [...offers.peek().results, ...offersResult.results] }
		: offersResult;

	if (categoriesResult) {
		phraseCategories.value = categoriesResult;
	}

	filters.historyPushState();
}
